import { z } from 'zod';

import { identitySchema } from '@/core/components';

import { UniversityConfig, UniversityConfigRequest } from './UniversityConfigTypes';

const namedSchema = identitySchema.extend({
  name: z.string(),
});

const seatSchema = namedSchema.nullable();

const officeMembersSchema = z.array(namedSchema);

export const universityConfigSchema = z.object({
  researchViceRector: seatSchema,
  internationalViceRector: seatSchema,
  researchOfficeMembers: officeMembersSchema,
  internationalOfficeMembers: officeMembersSchema,
});

export type UniversityConfigSchema = z.infer<typeof universityConfigSchema>;

export const defaultUniversityConfigValues: UniversityConfigSchema = {
  researchViceRector: null,
  internationalViceRector: null,
  researchOfficeMembers: [],
  internationalOfficeMembers: [],
};

function toSeatSchema(seat: UniversityConfig['researchViceRector']) {
  return seat == null ? null : { id: seat.id, name: seat.name };
}

function toSeatRequest(seat: UniversityConfigSchema['researchViceRector']) {
  return seat == null ? null : { id: seat.id };
}

function toMembersSchema(members: UniversityConfig['researchOfficeMembers']) {
  return members.map(member => ({ id: member.id, name: member.name }));
}

// The form holds the desired membership, so an emptied picker sends [] - the contract's "clear".
function toMemberIds(members: UniversityConfigSchema['researchOfficeMembers']) {
  return members.map(member => member.id);
}

export function toUniversityConfigSchema(config: UniversityConfig): UniversityConfigSchema {
  return {
    researchViceRector: toSeatSchema(config.researchViceRector),
    internationalViceRector: toSeatSchema(config.internationalViceRector),
    researchOfficeMembers: toMembersSchema(config.researchOfficeMembers),
    internationalOfficeMembers: toMembersSchema(config.internationalOfficeMembers),
  };
}

export function toUniversityConfigRequest(values: UniversityConfigSchema): UniversityConfigRequest {
  return {
    researchViceRector: toSeatRequest(values.researchViceRector),
    internationalViceRector: toSeatRequest(values.internationalViceRector),
    researchOfficeMemberIds: toMemberIds(values.researchOfficeMembers),
    internationalOfficeMemberIds: toMemberIds(values.internationalOfficeMembers),
  };
}
